import React, { useState, useEffect } from 'react';
import { Star, Pencil, Trash2, X, Check } from 'lucide-react';
import Card from './card';
import { supabase, safeSupabaseQuery } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface TestimonialCardProps {
  id?: string;
  name: string;
  role: string;
  company: string;
  content: string;
  avatar?: string;
  rating?: number;
  logoUrl?: string;
  onUpdate?: () => void;
}

export default function TestimonialCard({
  id,
  name,
  role,
  company,
  content,
  avatar,
  rating,
  logoUrl,
  onUpdate,
}: TestimonialCardProps) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name,
    role,
    company,
    content,
    avatar_url: avatar,
    rating,
    logo_url: logoUrl,
  });

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        setIsLoading(true);
        
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user) {
          console.warn('No authenticated user found');
          setIsAdmin(false);
          setIsLoading(false);
          return;
        }

        const { data: userData, error, isCorsError } = await safeSupabaseQuery(
          () => supabase
            .from('users')
            .select('role')
            .eq('id', user.id)
            .maybeSingle()
        );

        if (isCorsError) {
          console.warn('CORS error checking admin status, defaulting to non-admin');
          setIsAdmin(false);
          setIsLoading(false);
          return;
        }

        if (error) {
          console.error('Error fetching user role:', error.message);
          setIsAdmin(false);
          setIsLoading(false);
          return;
        }

        if (!userData) {
          console.warn('No user data found');
          setIsAdmin(false);
          setIsLoading(false);
          return;
        }

        setIsAdmin(userData.role === 'admin');
        setIsLoading(false);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        setIsLoading(false);
      }
    };

    checkAdmin();
  }, []);

  const handleDelete = async () => {
    if (!id) return;

    const confirmed = window.confirm('Êtes-vous sûr de vouloir supprimer ce témoignage ?');
    if (!confirmed) return;

    const { error, isCorsError } = await safeSupabaseQuery(
      () => supabase
        .from('testimonials')
        .delete()
        .eq('id', id)
    );

    if (isCorsError) {
      toast.error('Erreur de connexion. Veuillez vérifier votre configuration Supabase.');
      return;
    }

    if (error) {
      toast.error('Erreur lors de la suppression');
      return;
    }

    toast.success('Témoignage supprimé');
    if (onUpdate) onUpdate();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    const { error, isCorsError } = await safeSupabaseQuery(
      () => supabase
        .from('testimonials')
        .update({
          name: formData.name,
          role: formData.role,
          company: formData.company,
          content: formData.content,
          avatar_url: formData.avatar_url,
          rating: formData.rating,
          logo_url: formData.logo_url,
        })
        .eq('id', id)
    );

    if (isCorsError) {
      toast.error('Erreur de connexion. Veuillez vérifier votre configuration Supabase.');
      return;
    }

    if (error) {
      toast.error('Erreur lors de la mise à jour');
      return;
    }

    toast.success('Témoignage mis à jour');
    setIsEditing(false);
    if (onUpdate) onUpdate();
  };

  return (
    <Card className="h-full flex flex-col relative z-0 bg-white rounded-xl shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:bg-white hover:before:bg-transparent">
      {/* Logo */}
      {logoUrl && (
        <div className="px-6 pt-6">
          <img
            src={logoUrl}
            alt={company}
            className="h-8 object-contain"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}
      
      {/* Rating */}
      {rating && (
        <div className="px-6 pt-6 flex">
          {Array.from({ length: 5 }, (_, i) => (
            <Star 
              key={i}
              size={18} 
              className={i < rating ? "fill-yellow-400 text-yellow-400" : "text-neutral-200"}
            />
          ))}
        </div>
      )}
      
      {/* Content */}
      <div className="px-6 py-6 flex-grow">
        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Nom
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Rôle
              </label>
              <input
                type="text"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Entreprise
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Témoignage
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={4}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                URL Avatar
              </label>
              <input
                type="url"
                value={formData.avatar_url || ''}
                onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Note (1-5)
              </label>
              <input
                type="number"
                min="1"
                max="5"
                value={formData.rating || ''}
                onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                URL Logo
              </label>
              <input
                type="url"
                value={formData.logo_url || ''}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
              >
                <X size={18} />
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white hover:bg-primary-600 rounded-lg"
              >
                <Check size={18} />
              </button>
            </div>
          </form>
        ) : (
          <>
            <p className="italic text-neutral-700 mb-6">"{content}"</p>
            <div className="flex items-center">
              {avatar ? (
                <img
                  src={avatar}
                  alt={name}
                  className="w-12 h-12 rounded-full mr-4 object-cover"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-neutral-200 mr-4 flex items-center justify-center text-neutral-500 font-medium">
                  {name.charAt(0)}
                </div>
              )}
              <div>
                <div className="font-medium text-neutral-900">{name}</div>
                <div className="text-sm text-neutral-500">{role}, {company}</div>
              </div>
            </div>
          </>
        )}
      </div>

      {isAdmin && !isEditing && !isLoading && (
        <div className="px-6 pb-6 flex justify-end gap-2">
          <button
            onClick={() => setIsEditing(true)}
            className="p-2 text-primary hover:bg-primary-50 rounded-lg transition-colors"
          >
            <Pencil size={18} />
          </button>
          <button
            onClick={handleDelete}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 size={18} />
          </button>
        </div>
      )}
    </Card>
  );
}